'use client'

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSocket } from '@/providers/SocketContext'
import type { NotificationsChangedEvent } from '@/types/SocketEvents'
import { notificationKeys } from './useNotificationsQuery'

const REASONS_CONHECIDOS = new Set<NotificationsChangedEvent['reason']>(['new', 'updated'])

/**
 * Fronteira de socket: payload que não bate o contrato morre aqui.
 *
 * O back já acrescentou campo sem aviso antes (`transfer_kind` em
 * `channel_activity`), e invalidar por um `reason` desconhecido significaria
 * refetch do inbox inteiro a cada evento que ninguém previu.
 */
function isNotificationsChanged(raw: unknown): raw is NotificationsChangedEvent {
  if (typeof raw !== 'object' || raw === null) return false
  const reason = (raw as { reason?: unknown }).reason
  return typeof reason === 'string' &&
    REASONS_CONHECIDOS.has(reason as NotificationsChangedEvent['reason'])
}

/**
 * Mantém o inbox vivo pelo socket, em vez de esperar o poll.
 *
 * O ganho não é economizar requisição — é chegar com a **aba escondida**.
 * `useNotificationsOverview` tem `refetchIntervalInBackground: false`, então o
 * poll para quando o app vai pro segundo plano e o que chega só aparece quando o
 * usuário volta. A invalidação por evento não passa por esse gate, e é o que faz
 * a notificação do SO sair na hora.
 *
 * `invalidateQueries` (e não `setQueryData` como em `useChannelCountersLive`)
 * porque o evento **não carrega a notification**: só diz que a lista mudou.
 *
 * Guarda só em `socket`, nunca em `socketReady` — mesma razão dos outros
 * listeners do repo: no disconnect o cleanup removeria o listener e a reconexão
 * emite `connect` síncrono antes de o re-render reassinar.
 */
export function useNotificationsLive(): void {
  const { socket } = useSocket()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!socket) return

    const onChanged = (raw: unknown) => {
      if (!isNotificationsChanged(raw)) return
      // Raiz e não `overview()`: `new` mexe no contador e na lista de origem
      // quando é reentrega, e as mutations já invalidam por esta mesma chave.
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    }

    socket.on('notifications_changed', onChanged)
    return () => {
      socket.off('notifications_changed', onChanged)
    }
  }, [socket, queryClient])
}
